/**
 * Invented Kanjoy users (Memberships) for the Meridian Associates mockup
 * tenant — the `/settings/access` surface shipping in task 104d.1.
 *
 * Each row represents a Clerk user + org Membership: a Kanjoy user, their
 * role in the org, their (optional) link to an Employee row, and the
 * visibility scope their role allows (104 § Q6, Q7, Q8).
 *
 * Coverage (per the 104d.1 contract):
 *   - 2 owners, 2 admins, 3 members
 *   - 1 deactivated row (so the "Former members" section has content)
 *   - 1 linked to an existing employee (employeeId references EMPLOYEES)
 *   - 1 linked to a deactivated employee (EMPLOYEES_DEACTIVATED_AT key)
 *
 * All timestamps derive from FROZEN_NOW_MS — no new Date() calls.
 * All data is fictional.
 */

import { FROZEN_NOW_MS } from '@/lib/frozenTime';

const DAY_MS = 24 * 60 * 60 * 1000;

export type KanjoyUserRole = 'owner' | 'admin' | 'member';

/** Full 5-value enum shipped in 104f. Backend owns the source of truth
 * (`MembershipVisibilityScope` in Prisma). The fixture + hook carry the
 * same values through the wire so the page renders identical copy in
 * mockup mode. */
export type VisibilityScope = 'self' | 'all' | 'own_bu' | 'bu_list' | 'employee_list';

export interface KanjoyUser {
  /** Membership id (tenant-isolated; Clerk + Kanjoy composite) */
  id: string;
  /** Clerk user id */
  userId: string;
  email: string;
  name: string;
  role: KanjoyUserRole;
  createdAt: string;
  /** null for active rows, ISO string when the Membership was revoked */
  deactivatedAt: string | null;
  /** null when this Kanjoy user has no linked Employee record */
  employeeId: string | null;
  visibilityScope: VisibilityScope;
}

const daysAgo = (n: number): string => new Date(FROZEN_NOW_MS - n * DAY_MS).toISOString();

export const KANJOY_USERS: readonly KanjoyUser[] = [
  // ── Owners (2) ─────────────────────────────────────────────────────────
  {
    id: 'mem-sarah-chen',
    userId: 'usr-clerk-sarah-chen',
    email: 'schen@firm.com',
    name: 'Sarah Chen',
    role: 'owner',
    createdAt: daysAgo(365),
    deactivatedAt: null,
    // Linked to an existing, active Employee — proves the link column
    // resolves to a real `/settings/team` row.
    employeeId: 'emp-sarah-chen',
    visibilityScope: 'all',
  },
  {
    id: 'mem-priya-desai',
    userId: 'usr-clerk-priya-desai',
    email: 'pdesai@firm.com',
    name: 'Priya Desai',
    role: 'owner',
    createdAt: daysAgo(310),
    deactivatedAt: null,
    employeeId: null,
    visibilityScope: 'all',
  },
  // ── Admins (2) ─────────────────────────────────────────────────────────
  {
    id: 'mem-marcus-rivera',
    userId: 'usr-clerk-marcus-rivera',
    email: 'mrivera@firm.com',
    name: 'Marcus Rivera',
    role: 'admin',
    createdAt: daysAgo(240),
    deactivatedAt: null,
    employeeId: null,
    visibilityScope: 'own_bu',
  },
  {
    id: 'mem-lisa-thompson',
    userId: 'usr-clerk-lisa-thompson',
    email: 'lthompson@firm.com',
    name: 'Lisa Thompson',
    role: 'admin',
    createdAt: daysAgo(190),
    deactivatedAt: null,
    // Linked to an Employee that has been deactivated (see
    // EMPLOYEES_DEACTIVATED_AT). The Access page should still surface the
    // link but indicate the Employee is no longer active.
    employeeId: 'emp-lisa-thompson',
    visibilityScope: 'own_bu',
  },
  // ── Members (3) ────────────────────────────────────────────────────────
  {
    id: 'mem-aisha-patel',
    userId: 'usr-clerk-aisha-patel',
    email: 'apatel@firm.com',
    name: 'Aisha Patel',
    role: 'member',
    createdAt: daysAgo(120),
    deactivatedAt: null,
    employeeId: null,
    visibilityScope: 'self',
  },
  {
    id: 'mem-wei-zhang',
    userId: 'usr-clerk-wei-zhang',
    email: 'wzhang@firm.com',
    name: 'Wei Zhang',
    role: 'member',
    createdAt: daysAgo(90),
    deactivatedAt: null,
    employeeId: null,
    visibilityScope: 'self',
  },
  {
    id: 'mem-james-obrien',
    userId: 'usr-clerk-james-obrien',
    email: 'jobrien@firm.com',
    name: "James O'Brien",
    role: 'member',
    createdAt: daysAgo(45),
    deactivatedAt: null,
    employeeId: null,
    visibilityScope: 'self',
  },
  // ── Deactivated (1) — Former member ────────────────────────────────────
  {
    id: 'mem-elena-kowalski',
    userId: 'usr-clerk-elena-kowalski',
    email: 'ekowalski@firm.com',
    name: 'Elena Kowalski',
    role: 'member',
    createdAt: daysAgo(420),
    deactivatedAt: daysAgo(30),
    employeeId: null,
    visibilityScope: 'self',
  },
];
